/**
 * Typowany klient API dla SPA.
 *
 * Zrodlem prawdy o ksztaltach zadan i odpowiedzi jest `@shared/api-types` — ten
 * plik NIE definiuje wlasnych DTO poza trzema kopertami, ktore zyja dzisiaj w
 * `src/worker/routes/*` (patrz `CategoryTreeResponse`, `ProductWithImagesResponse`
 * ponizej).
 *
 * TODO(spec): ARCHITECTURE.md i PIPELINE.md mowia "typowana przez hono/client".
 * `hc<AppType>` wymaga zaimportowania `typeof app` z `src/worker/index.ts`, a to
 * wciagnelo by do projektu `web` typy `@cloudflare/workers-types` i pliki poza
 * `rootDir: src/web` — czyli zlamalo by granice architektoniczna wymuszona przez
 * trzy tsconfigi. Do czasu rozstrzygniecia trzymamy typowanie po stronie DTO z
 * `shared` (ten sam zestaw typow, ktory route'y deklaruja na swoich `c.json`).
 */

import type {
  Account,
  AddMemberRequest,
  AssistRequest,
  AssistResponse,
  ApproveAssetRequest,
  ApproveAssetResponse,
  Asset,
  BillingSummaryResponse,
  BlobTokenResponse,
  ByokRequest,
  ByokResponse,
  Category,
  ChooseVariantRequest,
  ChooseVariantResponse,
  Client,
  CompilePreviewRequest,
  CompilePreviewResponse,
  CostSummaryResponse,
  CreateAssetRequest,
  CreateAssetResponse,
  CreateCategoryRequest,
  CreateClientRequest,
  CreateGenerationRequest,
  CreateGenerationResponse,
  CreateProductRequest,
  CreateSceneRequest,
  CancelGenerationResponse,
  GalleryResponse,
  GenerationResponse,
  HealthResponse,
  LockSceneRequest,
  LockSceneResponse,
  MagicLinkRequest,
  MagicLinkResponse,
  MeResponse,
  Membership,
  ModelsResponse,
  OkResponse,
  Paginated,
  Product,
  ProductImage,
  ProductImageRole,
  PutSceneRevisionRequest,
  PutSceneRevisionResponse,
  RateVariantRequest,
  RateVariantResponse,
  CreatePresetRequest,
  Preset,
  Scene,
  SceneAutosaveQueuedResponse,
  SceneDraftStatusResponse,
  SceneResponse,
  SketchRequest,
  SketchResponse,
  TopupRequest,
  TopupResponse,
  UpdateAccountRequest,
  UpdateCategoryRequest,
  UpdateClientRequest,
  UpdateProductRequest,
  UploadProductImageResponse,
} from '@shared/api-types';
import type {
  AccountId,
  AssetId,
  CategoryId,
  ClientId,
  GenerationId,
  ProductId,
  ProductImageId,
  SceneId,
  UserId,
  VariantId,
} from '@shared/ids';

export const API_PREFIX = '/api';

/** Koperta drzewa kategorii (`CategoryTreeResponse` z `routes/categories.ts`). */
export type CategoryNode = Category & {
  readonly children: readonly CategoryNode[];
  readonly depth: number;
};

export type CategoryTreeResponse = {
  readonly items: readonly CategoryNode[];
  readonly flat: readonly Category[];
};

/** `GET /api/products/:id` (`ProductWithImagesResponse` z `routes/products.ts`). */
export type ProductWithImagesResponse = {
  readonly product: Product;
  readonly images: readonly ProductImage[];
};

/** Blad HTTP z API. `code` jest stabilnym identyfikatorem z `ApiErrorResponse`. */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: unknown;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    requestId?: string | null;
    details?: unknown;
  }) {
    super(init.message);
    this.name = 'ApiClientError';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId ?? null;
    this.details = init.details;
  }

  /** 501 = endpoint istnieje, ale nalezy do pozniejszego milestone'u. */
  get notImplemented(): boolean {
    return this.status === 501;
  }
}

export function isApiClientError(value: unknown): value is ApiClientError {
  return value instanceof ApiClientError;
}

type QueryValue = string | number | boolean | null | undefined;

function queryString(params: Readonly<Record<string, QueryValue>>): string {
  const search = new URLSearchParams();
  // Sortowanie kluczy trzyma stabilne URL-e (cache przegladarki, klucze Query).
  for (const key of Object.keys(params).sort()) {
    const value = params[key];
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text.length === 0 ? '' : `?${text}`;
}

type RequestInitLite = {
  readonly json?: unknown;
  readonly body?: BodyInit;
  readonly headers?: Readonly<Record<string, string>>;
  readonly signal?: AbortSignal;
};

async function request<T>(method: string, path: string, init: RequestInitLite = {}): Promise<T> {
  const headers = new Headers(init.headers);
  let body: BodyInit | undefined = init.body;
  if (init.json !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(init.json);
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    method,
    headers,
    // Sesja to cookie HttpOnly — bez `same-origin` nie wyjdzie z fetch().
    credentials: 'same-origin',
    ...(body === undefined ? {} : { body }),
    ...(init.signal === undefined ? {} : { signal: init.signal }),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text.length === 0 ? null : safeJson(text);

  if (!response.ok) {
    throw errorFromBody(response.status, parsed);
  }
  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { raw: text };
  }
}

function errorFromBody(status: number, parsed: unknown): ApiClientError {
  if (parsed !== null && typeof parsed === 'object' && 'error' in parsed) {
    const envelope = (parsed as { error: unknown }).error;
    if (envelope !== null && typeof envelope === 'object') {
      const record = envelope as Record<string, unknown>;
      return new ApiClientError({
        status,
        code: typeof record['code'] === 'string' ? record['code'] : 'http_error',
        message: typeof record['message'] === 'string' ? record['message'] : `HTTP ${String(status)}`,
        requestId: typeof record['requestId'] === 'string' ? record['requestId'] : null,
        details: record['details'],
      });
    }
  }
  return new ApiClientError({ status, code: 'http_error', message: `HTTP ${String(status)}` });
}

/**
 * URL bajtow bloba dla tagu `<img>`. Bez tokenu przechodzi droga sesyjna
 * (`routes/blobs.ts` autoryzuje klucz w D1), z tokenem — droga HMAC dla kluczy,
 * ktorych nie da sie powiazac z kontem po samym kluczu (np. contact sheet).
 */
export function blobUrl(key: string, token?: string | null): string {
  const encoded = key.split('/').map(encodeURIComponent).join('/');
  const suffix = token === undefined || token === null ? '' : `?t=${encodeURIComponent(token)}`;
  return `${API_PREFIX}/blob/${encoded}${suffix}`;
}

export const api = {
  health(): Promise<HealthResponse> {
    return request('GET', '/health');
  },

  // ── Auth i tozsamosc ──────────────────────────────────────────────────────
  auth: {
    /** JEDNO POLE EMAIL. Odpowiedz jest celowo nieinformatywna. */
    magicLink(body: MagicLinkRequest): Promise<MagicLinkResponse> {
      return request('POST', '/auth/magic-link', { json: body });
    },
    /**
     * Zuzycie tokenu z linku w mailu. `Accept: application/json` jest tu istotne:
     * bez niego Worker odpowiada przekierowaniem na `/` (link klikniety w
     * kliencie pocztowym).
     */
    callback(token: string): Promise<{ ok: true; userId: UserId; activeAccountId: AccountId | null }> {
      return request('GET', `/auth/callback${queryString({ token })}`, {
        headers: { accept: 'application/json' },
      });
    },
    logout(): Promise<OkResponse> {
      return request('POST', '/auth/logout');
    },
  },

  me(): Promise<MeResponse> {
    return request('GET', '/me');
  },

  accounts: {
    update(id: AccountId, body: UpdateAccountRequest): Promise<Account> {
      return request('PATCH', `/accounts/${id}`, { json: body });
    },
    members(id: AccountId): Promise<Paginated<Membership>> {
      return request('GET', `/accounts/${id}/members`);
    },
    addMember(id: AccountId, body: AddMemberRequest): Promise<unknown> {
      return request('POST', `/accounts/${id}/members`, { json: body });
    },
    removeMember(id: AccountId, userId: UserId): Promise<OkResponse> {
      return request('DELETE', `/accounts/${id}/members/${userId}`);
    },
  },

  // ── Katalog ───────────────────────────────────────────────────────────────
  clients: {
    list(options: { includeArchived?: boolean } = {}): Promise<Paginated<Client>> {
      return request('GET', `/clients${queryString({ includeArchived: options.includeArchived })}`);
    },
    get(id: ClientId): Promise<Client> {
      return request('GET', `/clients/${id}`);
    },
    create(body: CreateClientRequest): Promise<Client> {
      return request('POST', '/clients', { json: body });
    },
    update(id: ClientId, body: UpdateClientRequest): Promise<Client> {
      return request('PATCH', `/clients/${id}`, { json: body });
    },
  },

  categories: {
    tree(clientId: ClientId): Promise<CategoryTreeResponse> {
      return request('GET', `/categories${queryString({ clientId })}`);
    },
    create(body: CreateCategoryRequest): Promise<Category> {
      return request('POST', '/categories', { json: body });
    },
    update(id: CategoryId, body: UpdateCategoryRequest): Promise<Category> {
      return request('PATCH', `/categories/${id}`, { json: body });
    },
  },

  products: {
    list(
      params: { clientId?: ClientId; categoryId?: CategoryId; q?: string; cursor?: string; limit?: number } = {},
    ): Promise<Paginated<Product>> {
      return request('GET', `/products${queryString({ ...params })}`);
    },
    get(id: ProductId): Promise<ProductWithImagesResponse> {
      return request('GET', `/products/${id}`);
    },
    create(body: CreateProductRequest): Promise<Product> {
      return request('POST', '/products', { json: body });
    },
    update(id: ProductId, body: UpdateProductRequest): Promise<Product> {
      return request('PATCH', `/products/${id}`, { json: body });
    },
    /**
     * Cialo to SUROWE BAJTY pliku (kontrakt `routes/uploads.ts`), metadane ida w
     * query stringu. `sha256` policzone w przegladarce wlacza dedup bez drugiego
     * przeslania bajtow.
     */
    uploadImage(
      id: ProductId,
      file: Blob,
      options: { role: ProductImageRole; refPriority?: number; notes?: string; sha256?: string },
    ): Promise<UploadProductImageResponse> {
      const query = queryString({
        role: options.role,
        refPriority: options.refPriority,
        notes: options.notes,
      });
      return request('POST', `/products/${id}/images${query}`, {
        body: file,
        headers: {
          'content-type': file.type.length > 0 ? file.type : 'application/octet-stream',
          ...(options.sha256 === undefined ? {} : { 'x-blob-sha256': options.sha256 }),
        },
      });
    },
    cutout(id: ProductId, imageId: ProductImageId): Promise<{ image: ProductImage; cached: boolean; usd: number }> {
      return request('POST', `/products/${id}/images/${imageId}/cutout`);
    },
  },

  blobTokens(keys: readonly string[], ttlSeconds?: number): Promise<BlobTokenResponse> {
    return request('POST', '/blob-tokens', { json: { keys, ttlSeconds } });
  },

  // ── Sceny ─────────────────────────────────────────────────────────────────
  scenes: {
    list(
      params: { clientId?: ClientId; productId?: ProductId; limit?: number; cursor?: string } = {},
    ): Promise<Paginated<Scene>> {
      return request('GET', `/scenes${queryString({ ...params })}`);
    },
    get(id: SceneId): Promise<SceneResponse> {
      return request('GET', `/scenes/${id}`);
    },
    create(body: CreateSceneRequest): Promise<SceneResponse> {
      return request('POST', '/scenes', { json: body });
    },
    putRevision(id: SceneId, body: PutSceneRevisionRequest): Promise<PutSceneRevisionResponse> {
      return request('PUT', `/scenes/${id}/revisions`, { json: body });
    },
    /**
     * Autosave: zapis idzie do `SceneDoc`, ktory dobija wlasne okno ciszy.
     * Odpowiedz to 202 `{queued, flushAt, draftVersion, sessionId}` — rewizji
     * JESZCZE nie ma, wiec osobna sygnatura, a nie `putRevision` z rzutowaniem.
     *
     * `sessionId` w ciele jest OBOWIAZKOWY w praktyce: bez niego `SceneDoc`
     * nadaje draftowi jednorazowa tozsamosc i klient nie ma jak potwierdzic
     * WLASNEGO zapisu (patrz `sceneStore`).
     */
    putRevisionDebounced(
      id: SceneId,
      body: Omit<PutSceneRevisionRequest, 'debounce'>,
    ): Promise<SceneAutosaveQueuedResponse> {
      return request('PUT', `/scenes/${id}/revisions`, { json: { ...body, debounce: true } });
    },
    /**
     * Stan autosave'u. Zapytanie ZDARZENIOWE (po `flushAt`), nigdy interwalowe.
     *
     * To DRUGI KROK protokolu zapisu, nie diagnostyka: odpowiedz 202 z
     * `putRevisionDebounced` nie zna jeszcze `revisionId`, wiec dopiero
     * `recentResults` stad pozwalaja przesunac `headRevisionId` i zgasic
     * `dirty`. Wola to `sceneStore` przez zaleznosc `fetchDraftStatus`; para
     * `(sessionId, draftVersion)` mowi, czy widziany wynik dotyczy NASZEGO
     * draftu — cudzy wynik o wyzszej wersji znaczy, ze nasz przepadl.
     */
    draft(id: SceneId): Promise<SceneDraftStatusResponse> {
      return request('GET', `/scenes/${id}/draft`);
    },
    /** Wymus zapis draftu TERAZ — przed „Zablokuj kompozycje" i przed assist. */
    flush(id: SceneId): Promise<SceneDraftStatusResponse> {
      return request('POST', `/scenes/${id}/flush`);
    },
    /** Cartoon-podglad. `cached: true` = koszt $0. */
    sketch(id: SceneId, body: SketchRequest = {}): Promise<SketchResponse> {
      return request('POST', `/scenes/${id}/sketch`, { json: body });
    },
    /** Asystent LLM. `apply: false` (domyslnie) = propozycja bez zapisu rewizji. */
    assist(id: SceneId, body: AssistRequest): Promise<AssistResponse> {
      return request('POST', `/scenes/${id}/assist`, { json: body });
    },
    lock(id: SceneId, body: LockSceneRequest): Promise<LockSceneResponse> {
      return request('POST', `/scenes/${id}/lock`, { json: body });
    },
    /** Serwerowe echo kompilacji — do porownania z lokalnym `promptHash`. */
    compilePreview(id: SceneId, body: CompilePreviewRequest): Promise<CompilePreviewResponse> {
      return request('POST', `/scenes/${id}/compile-preview`, { json: body });
    },
  },

  // ── Presety ───────────────────────────────────────────────────────────────
  presets: {
    list(
      params: { kind?: Preset['kind']; clientId?: ClientId; globalOnly?: boolean; cursor?: string } = {},
    ): Promise<Paginated<Preset>> {
      return request('GET', `/presets${queryString({ ...params })}`);
    },
    create(body: CreatePresetRequest): Promise<Preset> {
      return request('POST', '/presets', { json: body });
    },
  },

  // ── Generacje ─────────────────────────────────────────────────────────────
  generations: {
    create(body: CreateGenerationRequest): Promise<CreateGenerationResponse> {
      return request('POST', '/generations', { json: body });
    },
    get(id: GenerationId, signal?: AbortSignal): Promise<GenerationResponse> {
      return request('GET', `/generations/${id}`, signal === undefined ? {} : { signal });
    },
    cancel(id: GenerationId): Promise<CancelGenerationResponse> {
      return request('POST', `/generations/${id}/cancel`);
    },
  },

  variants: {
    choose(id: VariantId, body: ChooseVariantRequest = {}): Promise<ChooseVariantResponse> {
      return request('POST', `/variants/${id}/choose`, { json: body });
    },
    rate(id: VariantId, body: RateVariantRequest): Promise<RateVariantResponse> {
      return request('POST', `/variants/${id}/rate`, { json: body });
    },
  },

  assets: {
    get(id: AssetId): Promise<Asset> {
      return request('GET', `/assets/${id}`);
    },
    create(body: CreateAssetRequest): Promise<CreateAssetResponse> {
      return request('POST', '/assets', { json: body });
    },
    approve(id: AssetId, body: ApproveAssetRequest = {}): Promise<ApproveAssetResponse> {
      return request('POST', `/assets/${id}/approve`, { json: body });
    },
  },

  /** M3 — dzisiaj 501; `GalleryPage` degraduje sie z komunikatem. */
  gallery(
    params: { clientId?: ClientId; productId?: ProductId; categoryId?: CategoryId; tier?: string; onlyChosen?: boolean; cursor?: string } = {},
  ): Promise<GalleryResponse> {
    return request('GET', `/gallery${queryString({ ...params })}`);
  },

  // ── Rejestr modeli, rozliczenia ───────────────────────────────────────────
  models(): Promise<ModelsResponse> {
    return request('GET', '/models');
  },

  billing: {
    summary(): Promise<BillingSummaryResponse> {
      return request('GET', '/billing/summary');
    },
    topup(body: TopupRequest): Promise<TopupResponse> {
      return request('POST', '/billing/topup', { json: body });
    },
  },

  byok(body: ByokRequest): Promise<ByokResponse> {
    return request('POST', '/byok', { json: body });
  },

  costs(params: { clientId?: ClientId; productId?: ProductId; period?: string } = {}): Promise<CostSummaryResponse> {
    return request('GET', `/costs/summary${queryString({ ...params })}`);
  },
} as const;

/** Assety zwracane przez `POST /api/assets` maja `blobKey` gotowy do `blobUrl`. */
export type { Asset };
